import type { ExtractedDocument, Issue, NormRect } from '../types';

export interface ViewLine {
	index: number;
	/** Global offset (doc.text içinde). */
	start: number;
	text: string;
	page?: number;
	blockId: string;
	/** Satırın PDF bbox'ı (varsa). */
	rect?: NormRect;
}

/**
 * Belgeyi satır satır görüntülemek için düzleştirir.
 * PDF'te sayfa numarası ve geometry, DOCX/TXT'te kaynak satır numarası korunur.
 */
export function buildLineMap(doc: ExtractedDocument): ViewLine[] {
	const lines: ViewLine[] = [];
	let cursor = 0;
	let index = 0;

	for (const block of doc.blocks) {
		const blockStart = cursor;
		const text = block.text;
		let local = 0;

		while (local <= text.length) {
			const nl = text.indexOf('\n', local);
			const end = nl === -1 ? text.length : nl;
			const lineText = text.slice(local, end);
			if (lineText.trim().length > 0) {
				const rect = lineRect(block.lines, block.lineOffsets, local, lineText);
				lines.push({
					index: index++,
					start: blockStart + local,
					text: lineText,
					page: block.page,
					blockId: block.id,
					rect
				});
			}
			if (nl === -1) break;
			local = nl + 1;
		}

		cursor = blockStart + text.length + 2;
	}
	return lines;
}

function lineRect(
	blockLines: ExtractedDocument['blocks'][number]['lines'],
	offsets: number[] | undefined,
	localStart: number,
	lineText: string
): NormRect | undefined {
	if (!blockLines || blockLines.length === 0) return undefined;
	for (let i = 0; i < blockLines.length; i++) {
		if (offsets && offsets.length === blockLines.length) {
			const start = offsets[i];
			const end = i + 1 < offsets.length ? offsets[i + 1] : localStart + lineText.length + 1;
			if (localStart >= start && localStart < end) {
				const l = blockLines[i];
				if (!l.pageWidth || !l.pageHeight) return undefined;
				return {
					x: l.x / l.pageWidth,
					y: (l.pageHeight - (l.y + l.height)) / l.pageHeight,
					w: l.width / l.pageWidth,
					h: l.height / l.pageHeight
				};
			}
		}
	}
	return undefined;
}

export interface LineRange {
	start: number;
	end: number;
	issue: Issue;
}

/** Issue'ları satır aralıklarına çevirir (global offset). */
export function issueRanges(issues: Issue[], doc: ExtractedDocument): LineRange[] {
	const ranges: LineRange[] = [];
	for (const issue of issues) {
		const start = resolveGlobalStart(issue, doc);
		if (start === undefined) continue;
		const len =
			issue.location.charEnd !== undefined ? issue.location.charEnd - (issue.location.charStart ?? 0) : 1;
		ranges.push({ start, end: start + Math.max(1, len), issue });
	}
	return ranges.sort((a, b) => a.start - b.start);
}

function resolveGlobalStart(issue: Issue, doc: ExtractedDocument): number | undefined {
	const loc = issue.location;
	if (loc.charStart === undefined) return undefined;

	// Blok kimliği varsa kesin çözüm (DOCX/TXT ve PDF için de geçerli)
	if (loc.blockId) {
		const idx = doc.blocks.findIndex((b) => b.id === loc.blockId);
		if (idx >= 0) return (doc.blockStarts[idx] ?? 0) + loc.charStart;
	}

	// PDF: sayfa numarasına göre bloğu bul
	if (loc.page !== undefined) {
		const idx = doc.blocks.findIndex((b) => b.page === loc.page);
		if (idx >= 0) return (doc.blockStarts[idx] ?? 0) + loc.charStart;
	}

	// Son çare: ilk blok
	return (doc.blockStarts[0] ?? 0) + loc.charStart;
}

/** Bir satır için vurgu parçalarını hesaplar. */
export function splitsForLine(
	line: ViewLine,
	ranges: LineRange[]
): { text: string; issues: Issue[] }[] {
	const lineStart = line.start;
	const lineEnd = lineStart + line.text.length;
	const relevant = ranges.filter((r) => r.start < lineEnd && r.end > lineStart);

	if (relevant.length === 0) return [{ text: line.text, issues: [] }];

	// Basit segmentasyon: her karakteri kapsayan issue listesi
	const bounds = new Set<number>([0, line.text.length]);
	for (const r of relevant) {
		bounds.add(Math.max(0, r.start - lineStart));
		bounds.add(Math.min(line.text.length, r.end - lineStart));
	}
	const sorted = [...bounds].sort((a, b) => a - b);
	const out: { text: string; issues: Issue[] }[] = [];
	for (let i = 0; i < sorted.length - 1; i++) {
		const s = sorted[i];
		const e = sorted[i + 1];
		if (e <= s) continue;
		const issues = relevant
			.filter((r) => r.start - lineStart < e && r.end - lineStart > s)
			.map((r) => r.issue);
		out.push({ text: line.text.slice(s, e), issues });
	}
	return out;
}
